import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import '../styles/GestionRutas.css';

const GestionRutas = () => {
  const [rutas, setRutas] = useState([]);
  const [rutaSeleccionada, setRutaSeleccionada] = useState(null);
  const [clientesRuta, setClientesRuta] = useState([]);
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState('lista'); // lista, mapa, asignar
  const [mostrarModalNuevaRuta, setMostrarModalNuevaRuta] = useState(false);
  const [mostrarModalEditarRuta, setMostrarModalEditarRuta] = useState(false);
  const [nuevaRuta, setNuevaRuta] = useState({
    nombre: '',
    descripcion: '',
    cobrador_id: '',
    zona: '',
    dia_cobro: '',
    frecuencia: 'semanal',
    meta_diaria: '',
    color: 'blue'
  });
  const [edicionRuta, setEdicionRuta] = useState(null);
  const [seleccionClientes, setSeleccionClientes] = useState([]);
  const [filtros, setFiltros] = useState({
    busqueda: '',
    zona: 'todas',
    estado: 'todas',
    frecuencia: 'todas',
    dia: 'todas',
    cobrador: 'todos'
  });

  const formatCurrency = (valor) => {
    const numero = Number(valor) || 0;
    return numero.toLocaleString('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 2
    });
  };

  useEffect(() => {
    cargarRutas();
    cargarEstadisticas();
    cargarClientesDisponibles();
  }, []);

  useEffect(() => {
    if (rutaSeleccionada) {
      cargarClientesRuta(rutaSeleccionada.id);
    }
  }, [rutaSeleccionada]);

  useEffect(() => {
    setSeleccionClientes([]);
  }, [rutaSeleccionada]);

  const zonasDisponibles = useMemo(() => {
    const zonas = rutas
      .map(r => r.zona)
      .filter(z => z && z.trim() !== '');
    return Array.from(new Set(zonas));
  }, [rutas]);

  const cobradoresDisponibles = useMemo(() => {
    const cobradores = rutas
      .map(r => r.cobrador_nombre)
      .filter(c => c && c.trim() !== '');
    return Array.from(new Set(cobradores));
  }, [rutas]);

  const diasDisponibles = useMemo(() => {
    const dias = rutas
      .map(r => r.dia_cobro)
      .filter(d => d && d.trim() !== '');
    return Array.from(new Set(dias));
  }, [rutas]);

  const frecuenciasDisponibles = useMemo(() => {
    const frecuencias = rutas
      .map(r => r.frecuencia)
      .filter(f => f && f.trim() !== '');
    return Array.from(new Set(frecuencias));
  }, [rutas]);

  const estadosDisponibles = useMemo(() => {
    const estados = rutas
      .map(r => r.estado)
      .filter(e => e && e.trim() !== '');
    return Array.from(new Set(estados));
  }, [rutas]);

  const rutasFiltradas = useMemo(() => {
    return rutas.filter(ruta => {
      const texto = filtros.busqueda.toLowerCase();
      const coincideBusqueda =
        !texto ||
        ruta.nombre?.toLowerCase().includes(texto) ||
        ruta.zona?.toLowerCase().includes(texto) ||
        ruta.cobrador_nombre?.toLowerCase().includes(texto);

      const coincideZona =
        filtros.zona === 'todas' || ruta.zona === filtros.zona;
      const coincideEstado =
        filtros.estado === 'todas' || ruta.estado === filtros.estado;
      const coincideFrecuencia =
        filtros.frecuencia === 'todas' || ruta.frecuencia === filtros.frecuencia;
      const coincideDia =
        filtros.dia === 'todas' || ruta.dia_cobro === filtros.dia;
      const coincideCobrador =
        filtros.cobrador === 'todos' || ruta.cobrador_nombre === filtros.cobrador;

      return (
        coincideBusqueda &&
        coincideZona &&
        coincideEstado &&
        coincideFrecuencia &&
        coincideDia &&
        coincideCobrador
      );
    });
  }, [rutas, filtros]);

  useEffect(() => {
    if (rutaSeleccionada && !rutasFiltradas.find(r => r.id === rutaSeleccionada.id)) {
      setRutaSeleccionada(rutasFiltradas[0] || null);
    } else if (!rutaSeleccionada && rutasFiltradas.length > 0) {
      setRutaSeleccionada(rutasFiltradas[0]);
    }
  }, [rutasFiltradas, rutaSeleccionada]);

  const cargarRutas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/rutas');

      if (response.data.success) {
        setRutas(response.data.rutas);
        if (!rutaSeleccionada && response.data.rutas.length > 0) {
          setRutaSeleccionada(response.data.rutas[0]);
        }
      }
    } catch (error) {
      console.error('Error cargando rutas:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarClientesRuta = async (rutaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/rutas/${rutaId}/clientes`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setClientesRuta(response.data.clientes);
      }
    } catch (error) {
      console.error('Error cargando clientes de ruta:', error);
    }
  };

  const cargarClientesDisponibles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        '/rutas/clientes-disponibles',
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setClientesDisponibles(response.data.clientes);
      }
    } catch (error) {
      console.error('Error cargando clientes disponibles:', error);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        '/rutas/estadisticas',
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setEstadisticas(response.data.estadisticas);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const crearRuta = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await api.post(
        '/rutas',
        nuevaRuta,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('✅ Ruta creada correctamente');
      setMostrarModalNuevaRuta(false);
      setNuevaRuta({
        nombre: '',
        descripcion: '',
        cobrador_id: '',
        zona: '',
        dia_cobro: '',
        frecuencia: 'semanal',
        meta_diaria: '',
        color: 'blue'
      });
      cargarRutas();
      cargarEstadisticas();
    } catch (error) {
      alert('Error al crear ruta');
      console.error(error);
    }
  };

  const asignarClientes = async (clienteIds = []) => {
    if (!rutaSeleccionada) {
      alert('Selecciona una ruta primero');
      return;
    }

    const ids = clienteIds.length > 0 ? clienteIds : seleccionClientes;
    if (ids.length === 0) {
      alert('Selecciona al menos un cliente');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.post(
        `/rutas/${rutaSeleccionada.id}/asignar-clientes`,
        { cliente_ids: ids },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('✅ Clientes asignados correctamente');
      cargarRutas();
      cargarClientesRuta(rutaSeleccionada.id);
      cargarClientesDisponibles();
      cargarEstadisticas();
      setSeleccionClientes([]);
    } catch (error) {
      alert('Error al asignar clientes');
      console.error(error);
    }
  };

  const desasignarCliente = async (clienteId) => {
    if (!rutaSeleccionada) return;

    if (!window.confirm('¿Desasignar este cliente de la ruta?')) return;

    try {
      const token = localStorage.getItem('token');
      await api.delete(
        `/rutas/${rutaSeleccionada.id}/desasignar-cliente/${clienteId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('✅ Cliente desasignado');
      cargarRutas();
      cargarClientesRuta(rutaSeleccionada.id);
      cargarClientesDisponibles();
      cargarEstadisticas();
      setSeleccionClientes(prev => prev.filter(id => id !== clienteId));
    } catch (error) {
      alert('Error al desasignar cliente');
      console.error(error);
    }
  };

  const optimizarRuta = async () => {
    if (!rutaSeleccionada) return;

    if (!window.confirm('¿Optimizar el orden de visitas basándose en GPS?')) return;

    try {
      const token = localStorage.getItem('token');
      await api.post(
        `/rutas/${rutaSeleccionada.id}/optimizar`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('✅ Ruta optimizada correctamente');
      cargarClientesRuta(rutaSeleccionada.id);
    } catch (error) {
      alert('Error al optimizar ruta');
      console.error(error);
    }
  };

  const abrirEnGoogleMaps = () => {
    if (clientesRuta.length === 0) {
      alert('No hay clientes con GPS en esta ruta');
      return;
    }

    const puntos = clientesRuta
      .filter(c => c.latitud && c.longitud)
      .map(c => `${c.latitud},${c.longitud}`);

    if (puntos.length === 0) {
      alert('Ningún cliente tiene ubicación GPS');
      return;
    }

    const origen = puntos[0];
    const destino = puntos[puntos.length - 1];
    const waypoints = puntos.slice(1, -1).join('|');

    let url = `https://www.google.com/maps/dir/?api=1&origin=${origen}&destination=${destino}`;
    if (waypoints) {
      url += `&waypoints=${waypoints}`;
    }
    url += '&travelmode=driving';

    window.open(url, '_blank');
  };

  const abrirEditarRuta = () => {
    if (!rutaSeleccionada) return;
    setEdicionRuta({
      nombre: rutaSeleccionada.nombre || '',
      descripcion: rutaSeleccionada.descripcion || '',
      zona: rutaSeleccionada.zona || '',
      dia_cobro: rutaSeleccionada.dia_cobro || '',
      frecuencia: rutaSeleccionada.frecuencia || 'semanal',
      meta_diaria: rutaSeleccionada.meta_diaria || '',
      color: rutaSeleccionada.color || 'blue',
      estado: rutaSeleccionada.estado || 'activa',
      orden_prioridad: rutaSeleccionada.orden_prioridad || 1
    });
    setMostrarModalEditarRuta(true);
  };

  const handleEditarRuta = async (e) => {
    e.preventDefault();
    if (!rutaSeleccionada || !edicionRuta) return;

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...edicionRuta,
        meta_diaria: edicionRuta.meta_diaria ? Number(edicionRuta.meta_diaria) : null,
        orden_prioridad: edicionRuta.orden_prioridad ? Number(edicionRuta.orden_prioridad) : 1
      };

      await api.put(
        `/rutas/${rutaSeleccionada.id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('✅ Ruta actualizada correctamente');
      setMostrarModalEditarRuta(false);
      setEdicionRuta(null);
      cargarRutas();
      cargarEstadisticas();
    } catch (error) {
      alert('Error al actualizar la ruta');
      console.error(error);
    }
  };

  const toggleSeleccionCliente = (clienteId) => {
    setSeleccionClientes(prev => {
      if (prev.includes(clienteId)) {
        return prev.filter(id => id !== clienteId);
      }
      return [...prev, clienteId];
    });
  };

  const saldoSeleccionado = useMemo(() => {
    return clientesDisponibles
      .filter(c => seleccionClientes.includes(c.id))
      .reduce((acc, c) => acc + (Number(c.saldo_pendiente) || 0), 0);
  }, [clientesDisponibles, seleccionClientes]);

  return (
    <div className="gestion-rutas">
      <div className="page-header">
        <h1>🗺️ Gestión de Rutas</h1>
        <button className="btn-nueva-ruta" onClick={() => setMostrarModalNuevaRuta(true)}>
          ➕ Nueva Ruta
        </button>
      </div>

      {estadisticas && (
        <>
          <div className="estadisticas-grid">
            <div className="stat-card blue">
              <div className="stat-icon">🗺️</div>
              <div className="stat-content">
                <div className="stat-value">{estadisticas.total_rutas}</div>
                <div className="stat-label">Rutas Activas</div>
              </div>
            </div>

            <div className="stat-card green">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-value">{estadisticas.clientes_asignados}</div>
                <div className="stat-label">Clientes Asignados</div>
              </div>
            </div>

            <div className="stat-card orange">
              <div className="stat-icon">📍</div>
              <div className="stat-content">
                <div className="stat-value">{estadisticas.clientes_sin_asignar}</div>
                <div className="stat-label">Sin Asignar</div>
              </div>
            </div>

            <div className="stat-card purple">
              <div className="stat-icon">👨‍💼</div>
              <div className="stat-content">
                <div className="stat-value">{estadisticas.cobradores_activos}</div>
                <div className="stat-label">Cobradores Activos</div>
              </div>
            </div>

            <div className="stat-card teal">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-value">{formatCurrency(estadisticas.meta_total)}</div>
                <div className="stat-label">Meta Diaria Total</div>
              </div>
            </div>

            <div className="stat-card dark">
              <div className="stat-icon">💵</div>
              <div className="stat-content">
                <div className="stat-value">{formatCurrency(estadisticas.saldo_pendiente_total)}</div>
                <div className="stat-label">Saldo Pendiente en Rutas</div>
              </div>
            </div>
          </div>

          <div className="estadisticas-detalle">
            {estadisticas?.por_cobrador?.length > 0 && (
              <div className="detalle-card">
                <div className="detalle-header">
                  <h3>📋 Resumen por Cobrador</h3>
                  <span>Total: {estadisticas.por_cobrador.length}</span>
                </div>
                <div className="detalle-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Cobrador</th>
                        <th>Clientes</th>
                        <th>Meta diaria</th>
                        <th>Saldo pendiente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estadisticas.por_cobrador.map((item) => (
                        <tr key={item.cobrador}>
                          <td>{item.cobrador}</td>
                          <td>{item.total_clientes}</td>
                          <td>{formatCurrency(item.meta_diaria)}</td>
                          <td>{formatCurrency(item.saldo_pendiente)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {estadisticas?.por_zona?.length > 0 && (
              <div className="detalle-card">
                <div className="detalle-header">
                  <h3>🗺️ Rutas por Zona</h3>
                  <span>Total: {estadisticas.por_zona.length}</span>
                </div>
                <div className="detalle-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Zona</th>
                        <th>Rutas</th>
                        <th>Clientes</th>
                        <th>Meta diaria</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estadisticas.por_zona.map((item) => (
                        <tr key={item.zona}>
                          <td>{item.zona}</td>
                          <td>{item.total_rutas}</td>
                          <td>{item.total_clientes}</td>
                          <td>{formatCurrency(item.meta_diaria)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div className="vista-tabs">
        <button
          className={vista === 'lista' ? 'active' : ''}
          onClick={() => setVista('lista')}
        >
          📋 Lista de Rutas
        </button>
        <button
          className={vista === 'asignar' ? 'active' : ''}
          onClick={() => setVista('asignar')}
        >
          ➕ Asignar Clientes
        </button>
        <button
          className={vista === 'mapa' ? 'active' : ''}
          onClick={() => setVista('mapa')}
        >
          🗺️ Ver en Mapa
        </button>
      </div>

      <div className="rutas-content">
        <div className="rutas-sidebar">
          <h3>Rutas ({rutasFiltradas.length}/{rutas.length})</h3>
          <div className="filtros-rutas">
            <input
              type="text"
              placeholder="Buscar por nombre, zona o cobrador"
              value={filtros.busqueda}
              onChange={e => setFiltros({...filtros, busqueda: e.target.value})}
            />
            <select
              value={filtros.cobrador}
              onChange={e => setFiltros({...filtros, cobrador: e.target.value})}
            >
              <option value="todos">Todos los cobradores</option>
              {cobradoresDisponibles.map(cobrador => (
                <option key={cobrador} value={cobrador}>{cobrador}</option>
              ))}
            </select>
            <select
              value={filtros.zona}
              onChange={e => setFiltros({...filtros, zona: e.target.value})}
            >
              <option value="todas">Todas las zonas</option>
              {zonasDisponibles.map(zona => (
                <option key={zona} value={zona}>{zona}</option>
              ))}
            </select>
            <div className="filtros-row">
              <select
                value={filtros.dia}
                onChange={e => setFiltros({...filtros, dia: e.target.value})}
              >
                <option value="todas">Todos los días</option>
                {diasDisponibles.map(dia => (
                  <option key={dia} value={dia}>{dia}</option>
                ))}
              </select>
              <select
                value={filtros.frecuencia}
                onChange={e => setFiltros({...filtros, frecuencia: e.target.value})}
              >
                <option value="todas">Todas las frecuencias</option>
                {frecuenciasDisponibles.map(freq => (
                  <option key={freq} value={freq}>{freq}</option>
                ))}
              </select>
            </div>
            <select
              value={filtros.estado}
              onChange={e => setFiltros({...filtros, estado: e.target.value})}
            >
              <option value="todas">Todos los estados</option>
              {estadosDisponibles.map(estado => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
            <button
              className="btn-reset-filtros"
              onClick={() => setFiltros({
                busqueda: '',
                zona: 'todas',
                estado: 'todas',
                frecuencia: 'todas',
                dia: 'todas',
                cobrador: 'todos'
              })}
            >
              Limpiar filtros
            </button>
          </div>
          <div className="rutas-list">
            {rutasFiltradas.map(ruta => (
              <div
                key={ruta.id}
                className={`ruta-item ${rutaSeleccionada?.id === ruta.id ? 'selected' : ''}`}
                onClick={() => setRutaSeleccionada(ruta)}
              >
                <div className="ruta-header">
                  <div
                    className="ruta-color"
                    style={{ backgroundColor: ruta.color }}
                  ></div>
                  <strong>{ruta.nombre}</strong>
                </div>
                <div className="ruta-info">
                  <span>👨‍💼 {ruta.cobrador_nombre || 'Sin asignar'}</span>
                  <span>📍 {ruta.total_clientes} clientes</span>
                  <span>📍 {ruta.zona || 'Sin zona'}</span>
                  <span>⏱️ {ruta.frecuencia}</span>
                </div>
              </div>
            ))}
            {rutasFiltradas.length === 0 && (
              <div className="sin-resultados">
                <p>No hay rutas que coincidan con los filtros.</p>
              </div>
            )}
          </div>
        </div>

        <div className="rutas-main">
          {loading && <p>Cargando información...</p>}

          {vista === 'lista' && rutaSeleccionada && (
            <div className="ruta-detalle">
              <div className="ruta-detalle-header">
                <h2>{rutaSeleccionada.nombre}</h2>
                <div className="ruta-actions">
                  <button className="btn-editar" onClick={abrirEditarRuta}>
                    ✏️ Editar Ruta
                  </button>
                  <button className="btn-optimizar" onClick={optimizarRuta}>
                    🎯 Optimizar Ruta
                  </button>
                  <button className="btn-maps" onClick={abrirEnGoogleMaps}>
                    🗺️ Abrir en Google Maps
                  </button>
                </div>
              </div>

              <div className="ruta-info-grid">
                <div className="info-item">
                  <label>Cobrador:</label>
                  <span>{rutaSeleccionada.cobrador_nombre || 'Sin asignar'}</span>
                </div>
                <div className="info-item">
                  <label>Zona:</label>
                  <span>{rutaSeleccionada.zona || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Día de cobro:</label>
                  <span>{rutaSeleccionada.dia_cobro || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Frecuencia:</label>
                  <span>{rutaSeleccionada.frecuencia}</span>
                </div>
                <div className="info-item">
                  <label>Total clientes:</label>
                  <span>{rutaSeleccionada.total_clientes}</span>
                </div>
                <div className="info-item">
                  <label>Pendiente:</label>
                  <span>{formatCurrency(rutaSeleccionada.total_pendiente)}</span>
                </div>
                <div className="info-item">
                  <label>Meta diaria:</label>
                  <span>{formatCurrency(rutaSeleccionada.meta_diaria)}</span>
                </div>
                <div className="info-item">
                  <label>Estado:</label>
                  <span className={`estado-pill ${rutaSeleccionada.estado}`}>
                    {rutaSeleccionada.estado}
                  </span>
                </div>
              </div>

              <h3>Clientes de la Ruta ({clientesRuta.length})</h3>

              {clientesRuta.length === 0 ? (
                <div className="empty-state">
                  <p>📭 No hay clientes asignados a esta ruta</p>
                  <button onClick={() => setVista('asignar')}>
                    Asignar Clientes
                  </button>
                </div>
              ) : (
                <div className="clientes-ruta-table">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Cliente</th>
                        <th>Cédula</th>
                        <th>Teléfono</th>
                        <th>Dirección</th>
                        <th>Préstamos</th>
                        <th>Saldo</th>
                        <th>GPS</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientesRuta.map((cliente, index) => (
                        <tr key={cliente.id}>
                          <td><strong>{cliente.orden || index + 1}</strong></td>
                          <td>{cliente.nombres} {cliente.apellidos}</td>
                          <td>{cliente.cedula}</td>
                          <td>
                            {cliente.celular || cliente.telefono ? (
                              <a href={`tel:${cliente.celular || cliente.telefono}`}>
                                📞 {cliente.celular || cliente.telefono}
                              </a>
                            ) : '-'}
                          </td>
                          <td className="direccion-cell">{cliente.direccion || '-'}</td>
                          <td>{cliente.prestamos_activos}</td>
                          <td>{formatCurrency(cliente.saldo_pendiente)}</td>
                          <td>
                            {cliente.latitud && cliente.longitud ? (
                              <span className="gps-badge ok">✅</span>
                            ) : (
                              <span className="gps-badge no">❌</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn-desasignar"
                              onClick={() => desasignarCliente(cliente.id)}
                            >
                              ❌
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {vista === 'asignar' && rutaSeleccionada && (
            <div className="asignar-clientes">
              <h2>Asignar Clientes a: {rutaSeleccionada.nombre}</h2>
              <div className="asignar-actions">
                <div>
                  <strong>Seleccionados: {seleccionClientes.length}</strong>
                  {seleccionClientes.length > 0 && (
                    <span className="asignar-monto">
                      {formatCurrency(saldoSeleccionado)}
                    </span>
                  )}
                </div>
                <div className="asignar-buttons">
                  <button
                    className="btn-asignar-seleccion"
                    onClick={() => asignarClientes(seleccionClientes)}
                  >
                    ➕ Asignar seleccionados
                  </button>
                  <button
                    className="btn-limpiar-seleccion"
                    onClick={() => setSeleccionClientes([])}
                  >
                    Limpiar selección
                  </button>
                </div>
              </div>

              <div className="clientes-disponibles-grid">
                {clientesDisponibles.length === 0 ? (
                  <div className="empty-state">
                    <p>✅ Todos los clientes están asignados</p>
                  </div>
                ) : (
                  clientesDisponibles.map(cliente => (
                    <div
                      key={cliente.id}
                      className={`cliente-disponible-card ${seleccionClientes.includes(cliente.id) ? 'seleccionado' : ''}`}
                    >
                      <div className="cliente-info">
                        <label className="checkbox-seleccion">
                          <input
                            type="checkbox"
                            checked={seleccionClientes.includes(cliente.id)}
                            onChange={() => toggleSeleccionCliente(cliente.id)}
                          />
                          Seleccionar
                        </label>
                        <strong>{cliente.nombres} {cliente.apellidos}</strong>
                        <span className="cliente-cedula">{cliente.cedula}</span>
                        <span className="cliente-direccion">{cliente.direccion}</span>
                        <div className="cliente-stats">
                          <span>💰 {formatCurrency(cliente.saldo_pendiente)}</span>
                          <span>
                            {cliente.latitud ? '📍 GPS ✅' : '📍 Sin GPS'}
                          </span>
                        </div>
                      </div>
                      <button
                        className="btn-asignar"
                        onClick={() => asignarClientes([cliente.id])}
                      >
                        ➕ Asignar
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {vista === 'mapa' && rutaSeleccionada && (
            <div className="vista-mapa">
              <h2>Mapa de Ruta: {rutaSeleccionada.nombre}</h2>

              <div className="mapa-placeholder">
                <p>🗺️ Vista de Mapa</p>
                <p>Mostrando {clientesRuta.filter(c => c.latitud).length} clientes con GPS</p>

                <button className="btn-maps-grande" onClick={abrirEnGoogleMaps}>
                  🌐 Abrir Ruta Completa en Google Maps
                </button>

                <div className="clientes-mapa-list">
                  {clientesRuta
                    .filter(c => c.latitud && c.longitud)
                    .map((cliente, index) => (
                      <div key={cliente.id} className="cliente-mapa-item">
                        <span className="numero-orden">{cliente.orden || index + 1}</span>
                        <div className="cliente-mapa-info">
                          <strong>{cliente.nombres} {cliente.apellidos}</strong>
                          <span>{cliente.direccion}</span>
                          <a
                            href={`https://www.google.com/maps?q=${cliente.latitud},${cliente.longitud}`}
                            target="_blank"
                            rel="noreferrer"
                            className="ver-en-maps"
                          >
                            Ver en Maps →
                          </a>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {mostrarModalNuevaRuta && (
        <>
          <div className="modal-backdrop" onClick={() => setMostrarModalNuevaRuta(false)}></div>
          <div className="modal-nueva-ruta">
            <div className="modal-header">
              <h3>➕ Nueva Ruta</h3>
              <button className="btn-close" onClick={() => setMostrarModalNuevaRuta(false)}>×</button>
            </div>

            <form onSubmit={crearRuta} className="form-nueva-ruta">
              <div className="form-group">
                <label>Nombre de la Ruta *</label>
                <input
                  type="text"
                  value={nuevaRuta.nombre}
                  onChange={e => setNuevaRuta({...nuevaRuta, nombre: e.target.value})}
                  required
                  placeholder="Ej: Ruta Centro"
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={nuevaRuta.descripcion}
                  onChange={e => setNuevaRuta({...nuevaRuta, descripcion: e.target.value})}
                  placeholder="Descripción de la ruta..."
                  rows="3"
                ></textarea>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zona</label>
                  <input
                    type="text"
                    value={nuevaRuta.zona}
                    onChange={e => setNuevaRuta({...nuevaRuta, zona: e.target.value})}
                    placeholder="Ej: Zona Colonial"
                  />
                </div>

                <div className="form-group">
                  <label>Color</label>
                  <select
                    value={nuevaRuta.color}
                    onChange={e => setNuevaRuta({...nuevaRuta, color: e.target.value})}
                  >
                    <option value="blue">🔵 Azul</option>
                    <option value="red">🔴 Rojo</option>
                    <option value="green">🟢 Verde</option>
                    <option value="orange">🟠 Naranja</option>
                    <option value="purple">🟣 Morado</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Día de Cobro</label>
                  <select
                    value={nuevaRuta.dia_cobro}
                    onChange={e => setNuevaRuta({...nuevaRuta, dia_cobro: e.target.value})}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="lunes">Lunes</option>
                    <option value="martes">Martes</option>
                    <option value="miercoles">Miércoles</option>
                    <option value="jueves">Jueves</option>
                    <option value="viernes">Viernes</option>
                    <option value="sabado">Sábado</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Frecuencia</label>
                  <select
                    value={nuevaRuta.frecuencia}
                    onChange={e => setNuevaRuta({...nuevaRuta, frecuencia: e.target.value})}
                  >
                    <option value="diaria">Diaria</option>
                    <option value="semanal">Semanal</option>
                    <option value="quincenal">Quincenal</option>
                    <option value="mensual">Mensual</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Meta Diaria (RD$)</label>
                <input
                  type="number"
                  value={nuevaRuta.meta_diaria}
                  onChange={e => setNuevaRuta({...nuevaRuta, meta_diaria: e.target.value})}
                  placeholder="Ej: 15000"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setMostrarModalNuevaRuta(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Crear Ruta
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {mostrarModalEditarRuta && edicionRuta && (
        <>
          <div className="modal-backdrop" onClick={() => setMostrarModalEditarRuta(false)}></div>
          <div className="modal-nueva-ruta">
            <div className="modal-header">
              <h3>✏️ Editar Ruta</h3>
              <button className="btn-close" onClick={() => setMostrarModalEditarRuta(false)}>×</button>
            </div>
            <form onSubmit={handleEditarRuta} className="form-nueva-ruta">
              <div className="form-group">
                <label>Nombre de la Ruta *</label>
                <input
                  type="text"
                  value={edicionRuta.nombre}
                  onChange={e => setEdicionRuta({...edicionRuta, nombre: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={edicionRuta.descripcion}
                  onChange={e => setEdicionRuta({...edicionRuta, descripcion: e.target.value})}
                  rows="3"
                ></textarea>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zona</label>
                  <input
                    type="text"
                    value={edicionRuta.zona}
                    onChange={e => setEdicionRuta({...edicionRuta, zona: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <select
                    value={edicionRuta.color}
                    onChange={e => setEdicionRuta({...edicionRuta, color: e.target.value})}
                  >
                    <option value="blue">🔵 Azul</option>
                    <option value="red">🔴 Rojo</option>
                    <option value="green">🟢 Verde</option>
                    <option value="orange">🟠 Naranja</option>
                    <option value="purple">🟣 Morado</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Día de Cobro</label>
                  <select
                    value={edicionRuta.dia_cobro}
                    onChange={e => setEdicionRuta({...edicionRuta, dia_cobro: e.target.value})}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="lunes">Lunes</option>
                    <option value="martes">Martes</option>
                    <option value="miercoles">Miércoles</option>
                    <option value="jueves">Jueves</option>
                    <option value="viernes">Viernes</option>
                    <option value="sabado">Sábado</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Frecuencia</label>
                  <select
                    value={edicionRuta.frecuencia}
                    onChange={e => setEdicionRuta({...edicionRuta, frecuencia: e.target.value})}
                  >
                    <option value="diaria">Diaria</option>
                    <option value="semanal">Semanal</option>
                    <option value="quincenal">Quincenal</option>
                    <option value="mensual">Mensual</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Meta Diaria (RD$)</label>
                  <input
                    type="number"
                    value={edicionRuta.meta_diaria}
                    onChange={e => setEdicionRuta({...edicionRuta, meta_diaria: e.target.value})}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    value={edicionRuta.estado}
                    onChange={e => setEdicionRuta({...edicionRuta, estado: e.target.value})}
                  >
                    <option value="activa">Activa</option>
                    <option value="pausada">Pausada</option>
                    <option value="finalizada">Finalizada</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Orden de prioridad</label>
                <input
                  type="number"
                  min="1"
                  value={edicionRuta.orden_prioridad}
                  onChange={e => setEdicionRuta({...edicionRuta, orden_prioridad: e.target.value})}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setMostrarModalEditarRuta(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Guardar cambios
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default GestionRutas;

